import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { EN_COUNTERPARTS } from '@/data/englishLandings';
import { SITE_URL } from '@/lib/siteDomains';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  alternateLanguages?: {
    sr?: string;
    en?: string;
    ru?: string;
  };
  /** Explicit x-default URL. Defaults to the canonical. */
  xDefault?: string;
  /**
   * Force the page language regardless of the UI language selected in the
   * switcher (used by the dedicated English landing pages).
   */
  lang?: 'sr' | 'en' | 'ru';
  /**
   * Limit which hreflang tags are emitted. Useful when a page only has
   * translations in a subset of languages — emitting hreflang for a
   * language with no real translation hurts SEO. Defaults to all three.
   */
  availableLanguages?: Array<'sr' | 'en' | 'ru'>;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
  };
}

const SEOHead = ({ 
  title, 
  description, 
  canonical, 
  ogImage = "https://physiowrld.rs/images/physiowrld-logo.webp",
  ogType = "website",
  noindex = false,
  alternateLanguages,
  xDefault,
  lang: forcedLang,
  availableLanguages,
  article
}: SEOHeadProps) => {
  const { i18n } = useTranslation();
  const lang = forcedLang || i18n.language;
  const emit = (l: 'sr' | 'en' | 'ru') =>
    !availableLanguages || availableLanguages.includes(l);
  
  const requestedPath = canonical.startsWith('http')
    ? new URL(canonical).pathname
    : canonical.startsWith('/') ? canonical : `/${canonical}`;
  const enCounterpart = EN_COUNTERPARTS[requestedPath];
  const canonicalPath = lang === 'en' && enCounterpart ? enCounterpart : requestedPath;
  // Single canonical domain: everything lives on physiowrld.rs.
  const canonicalUrl = `${SITE_URL}${canonicalPath === '/' ? '/' : canonicalPath}`;

  // If this Serbian page has a dedicated English landing page, point
  // hreflang="en" at it so Google treats them as one language cluster.
  const hreflangUrls = alternateLanguages || {
    sr: `${SITE_URL}${requestedPath}`,
    en: `${SITE_URL}${enCounterpart || requestedPath}`,
    ru: `${SITE_URL}${requestedPath}`,
  };
  const xDefaultUrl = xDefault || `${SITE_URL}${requestedPath}`;
  
  // Locale mapping
  const localeMap = {
    sr: 'sr_RS',
    en: 'en_GB',
    ru: 'ru_RU'
  };
  const ogLocale = localeMap[lang as keyof typeof localeMap] || 'sr_RS';
  
  return (
    <Helmet>
      <html lang={lang === 'sr' ? 'sr-RS' : lang === 'ru' ? 'ru-RU' : 'en-GB'} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Alternate languages — only emit for languages where content actually exists */}
      {emit('sr') && <link rel="alternate" hrefLang="sr" href={hreflangUrls.sr || canonicalUrl} />}
      {emit('en') && <link rel="alternate" hrefLang="en" href={hreflangUrls.en || canonicalUrl} />}
      {emit('ru') && <link rel="alternate" hrefLang="ru" href={hreflangUrls.ru || canonicalUrl} />}
      <link rel="alternate" hrefLang="x-default" href={xDefaultUrl} />
      
      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="PHYSIOWRLD" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={ogLocale} />
      {lang !== 'sr' && <meta property="og:locale:alternate" content="sr_RS" />}
      {lang !== 'en' && <meta property="og:locale:alternate" content="en_GB" />}
      {lang !== 'ru' && <meta property="og:locale:alternate" content="ru_RU" />}
      
      {/* Article specific OG tags */}
      {ogType === 'article' && article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {ogType === 'article' && article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {ogType === 'article' && article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      {ogType === 'article' && article?.section && (
        <meta property="article:section" content={article.section} />
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@physiowrld" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
};

export default SEOHead;
